import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import {
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";

import politic from "public/politic.svg";
import social from "public/social.svg";
import economic from "public/economic.svg";
import prophetic from "public/prophetic.svg";
import finance from "public/finance.svg";
import sport from "public/sport.svg";
import scientific from "public/scientific.svg";
import useSurveys from "hooks/surveys/useSurveys";
import SurveyCard from "components/card/SurveyCard";
import InfiniteScrollTrigger from "components/infinite-scroll/InfiniteScrollTrigger";
import { skeletonGenerator } from "components/infinite-scroll/helpers";
import ProgramSkeleton from "components/infinite-scroll/skeletons/ArticleSkeleton";
import { getSurveys } from "api-algolia/surveys.api";
import useSearch from "hooks/algolia/useSearch";
import useDarkMode from "hooks/useDarkMode";
import { MdLightMode, MdDarkMode } from "react-icons/md";

const CATEGORIES = [
  { value: "politic", title: "Քաղաքական", icon: politic },
  { value: "social", title: "Սոցիալական", icon: social },
  { value: "economic", title: "Տնտեսական", icon: economic },
  { value: "prophetic", title: "Մարգարեական", icon: prophetic },
  { value: "finance", title: "Ֆինանսական", icon: finance },
  { value: "sport", title: "Սպորտային", icon: sport },
  { value: "scientific", title: "Գիտական", icon: scientific },
];

const Predictors = () => {
  const router = useRouter();
  const { theme: appTheme, toggleTheme, mounted } = useDarkMode();
  const { predictor, categories, finished } = router.query;
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    predictorId: predictor,
    order: { orderBy: "createdAt", direction: "desc" },
  });
  const {
    surveys,
    count,
    isLoading,
    getNextPage,
    page,
    setPage,
    hasMoreItems,
  } = useSurveys(filters);

  useEffect(() => {
    if (categories) {
      setFilters((prev) => ({ ...prev, categories: categories.split(",") }));
    } else {
      setFilters((prev) => ({ ...prev, categories: [] }));
    }
  }, [categories]);

  useEffect(() => {
    if (finished === "true") {
      setFilters((prev) => ({ ...prev, finished: true }));
    } else {
      setFilters((prev) => ({ ...prev, finished: false }));
    }
  }, [finished]);

  const { setSearchText, searchText, searchResult, searchLoading } = useSearch({
    getData: getSurveys,
  });

  return (
    <div className="flex flex-col items-center justify-center mb-[24px] gap-[16px] md:gap-[24px] p-4 md:p-[24px] rounded-[24px] bg-[#030014]/60 backdrop-blur-xl border border-cyan-500/20 shadow-[0_0_30px_rgba(102,133,255,0.05)]">
      <div className="flex flex-col gap-[14px] w-full">
        <div className="flex flex-col md:flex-row justify-between gap-[16px] items-start md:items-center w-full">
          <div className="flex items-center gap-[12px]">
            <div className="relative text-[24px] xl:text-[34px] font-sans font-black tracking-tighter text-cyan-50 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">
              Կանխատեսումներ
            </div>
            {searchText && (
              <div className="flex items-center justify-center bg-cyan-500/20 border border-cyan-400 shadow-[0_0_10px_rgba(102,133,255,0.3)] rounded-[16px] w-[40px] h-[40px] text-cyan-50  text-[18px] font-bold">
                {searchResult.length}
              </div>
            )}
            {!searchText && !!count && (
              <div className="flex items-center justify-center bg-cyan-500/20 border border-cyan-400 shadow-[0_0_10px_rgba(102,133,255,0.3)] rounded-[16px] w-[40px] h-[40px] text-cyan-50  text-[18px] font-bold">
                {count}
              </div>
            )}
          </div>

          <div className="flex flex-row items-center gap-[12px] w-full md:w-auto">
            <TextField
              placeholder="Որոնում"
              type="text"
              autoComplete="off"
              sx={{
                backgroundColor: "rgba(102, 133, 255, 0.05)",
                borderRadius: "14px",
                "& fieldset": {
                  borderColor: "rgba(102, 133, 255, 0.2)",
                  transition: "all 0.3s",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(102, 133, 255, 0.5) !important",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(102, 133, 255, 0.8) !important",
                  boxShadow: "0 0 10px rgba(102, 133, 255, 0.2)",
                },
                "& .MuiInputBase-input": {
                  color: "#e2e8f0",
                  fontFamily: "var(--tw-)",
                  padding: "10px 14px",
                },
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full md:w-[250px]"
            />
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex items-center shrink-0 justify-center w-[44px] h-[44px] rounded-xl border transition-all duration-300 ${
                isExpanded
                  ? "bg-cyan-500/30 border-cyan-400 text-cyan-50 shadow-[0_0_15px_rgba(102,133,255,0.3)]"
                  : "bg-cyan-500/10 border-cyan-500/20 text-cyan-50 hover:bg-cyan-500/20"
              }`}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div
          className={`flex flex-col gap-[14px] w-full overflow-hidden transition-all duration-500 origin-top ${
            isExpanded ? "max-h-[1000px] opacity-100 mt-2" : "max-h-0 opacity-0"
          }`}
        >
          <div
            className={`${searchText && !searchLoading ? "hidden" : "flex"}`}
          >
            <FormControl
              sx={{
                width: "fit-content",
                backgroundColor: "rgba(102, 133, 255, 0.05)",
                borderRadius: "12px",
                padding: "0px",
              }}
              size="small"
            >
              <InputLabel
                id="order-select-label"
                sx={{ color: "rgba(102, 133, 255, 0.7)" }}
              >
                Տեսակավորում ըստ՝
              </InputLabel>
              <Select
                labelId="order-select-label"
                value={`${filters.order.orderBy}_${filters.order.direction}`}
                onChange={(e) => {
                  const [orderBy, direction] = e.target.value.split("_");
                  setFilters((prev) => ({
                    ...prev,
                    order: { orderBy, direction },
                  }));
                }}
                label="Տեսակավորում ըստ՝"
                sx={{
                  borderRadius: "12px",
                  padding: "0px",
                  color: "#e2e8f0",
                  fontFamily: "var(--tw-)",
                  "& fieldset": {
                    borderColor: "rgba(102, 133, 255, 0.2)",
                    transition: "all 0.3s",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(102, 133, 255, 0.5) !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(102, 133, 255, 0.8) !important",
                    boxShadow: "0 0 10px rgba(102, 133, 255, 0.2)",
                  },
                  "& .MuiSvgIcon-root": { color: "rgba(102, 133, 255, 0.8)" },
                }}
              >
                <MenuItem value="createdAt_desc">
                  Ստեղծման ամսաթվի (նոր)
                </MenuItem>
                <MenuItem value="createdAt_asc">
                  Ստեղծման ամսաթվի (հին)
                </MenuItem>
                <MenuItem value="deadline_desc">Վերջնաժամկետի (բարձր)</MenuItem>
                <MenuItem value="deadline_asc">Վերջնաժամկետի (ցածր)</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div
            className={`flex flex-wrap gap-[16px] md:gap-[24px] ${searchText && !searchLoading ? "hidden" : "flex"}`}
          >
            {predictor && surveys?.[0] && <>{surveys[0].predictor.name}</>}
            {CATEGORIES.map((category, index) => {
              return (
                <button
                  key={index}
                  onClick={() => {
                    if (filters.categories?.includes(category.value)) {
                      router.push({
                        pathname: "/surveys",
                        query: {
                          predictor,
                          finished,
                          categories: filters.categories
                            .filter((item) => item !== category.value)
                            .join(","),
                        },
                      });
                    } else {
                      router.push({
                        pathname: "/surveys",
                        query: {
                          predictor,
                          finished,
                          categories: [
                            ...[...(filters.categories || [])],
                            category.value,
                          ].join(","),
                        },
                      });
                    }
                  }}
                  className={`flex items-center gap-[10px] rounded-[14px] p-[12px] md:p-[16px] transition-all duration-500 border ${filters.categories?.includes(category.value) ? "bg-cyan-500/20 border-cyan-400 shadow-[inset_0_0_15px_rgba(102,133,255,0.2)] text-cyan-100" : "bg-[#030014]/40 border-cyan-500/20 text-slate-400 hover:border-cyan-400/60 hover:text-cyan-200"}`}
                >
                  <Image
                    width={0}
                    height={0}
                    sizes="100vh"
                    src={category.icon}
                    alt="person"
                    priority
                    className="xl:h-[26px] xl:w-[26px] h-[20px] w-[20px]"
                  />
                  <span className="text-[14px] hidden xl:block">
                    {category.title}
                  </span>
                </button>
              );
            })}
            <button
              onClick={() => {
                if (filters.finished) {
                  router.push({
                    pathname: "/surveys",
                    query: {
                      predictor,
                      categories,
                      finished: false,
                    },
                  });
                } else {
                  router.push({
                    pathname: "/surveys",
                    query: {
                      predictor,
                      categories,
                      finished: true,
                    },
                  });
                }
              }}
              className={`flex items-center gap-[10px] border rounded-[14px] p-[12px] md:p-[16px] transition-all duration-500 ${filters.finished ? "bg-cyan-500/20 border-cyan-400 shadow-[inset_0_0_15px_rgba(102,133,255,0.2)] text-cyan-100" : "bg-[#030014]/40 border-cyan-500/20 text-slate-400 hover:border-cyan-400/60 hover:text-cyan-200"}`}
            >
              <span className="text-[14px]">Միայն Ավարտվածները</span>
            </button>
          </div>
        </div>
      </div>
      {!!searchResult.length && searchText && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-[24px] w-full">
          {searchResult.map((survey) => {
            return <SurveyCard key={survey.objectID} survey={survey} />;
          })}
        </div>
      )}
      <div
        className={`grid grid-cols-1 xl:grid-cols-2 gap-[24px] w-full ${searchText && !searchLoading ? "hidden" : "grid"}`}
      >
        {surveys.map((survey) => {
          return <SurveyCard key={survey.id} survey={survey} />;
        })}
        {isLoading && skeletonGenerator(<ProgramSkeleton />)}
        <InfiniteScrollTrigger
          isLoading={isLoading}
          hasMoreItems={hasMoreItems}
          callback={getNextPage}
          page={page}
          setPage={setPage}
        />
      </div>
    </div>
  );
};

export default Predictors;
